import type { MeshBasicMaterial } from 'three';

// Injects a custom shader chunk on the given MeshBasicMaterial that
// blends the backface toward white — cheaper glass illusion than a
// full MeshPhysicalMaterial while keeping the wall texture crisp.
function applyGlassBackfaceShader(material: MeshBasicMaterial) {
    material.userData.backfaceIntensity = 0.75;
    material.userData.noiseIntensity = 1.0;
    material.userData.noiseScale = 1.0;
    material.userData.time = 125;

    material.onBeforeCompile = (shader) => {
        shader.uniforms.backfaceIntensity = { value: material.userData.backfaceIntensity };
        shader.uniforms.noiseIntensity = { value: material.userData.noiseIntensity };
        shader.uniforms.noiseScale = { value: material.userData.noiseScale };
        shader.uniforms.time = { value: 0.0 };

        shader.vertexShader = shader.vertexShader.replace(
            '#include <common>',
            /* glsl */ `
                #include <common>
                varying vec2 vN;
                varying vec3 vWorldNormal;
                varying vec3 vWorldPosition;
            `,
        );
        shader.vertexShader = shader.vertexShader.replace(
            '#include <begin_vertex>',
            /* glsl */ `
                #include <begin_vertex>
                vN = uv;
                vWorldNormal = normalize((modelMatrix * vec4(normal, 0.0)).xyz);
                vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
            `,
        );
        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <common>',
            /* glsl */ `
                #include <common>
                varying vec2 vN;
                varying vec3 vWorldNormal;
                varying vec3 vWorldPosition;

                uniform float backfaceIntensity;
                uniform float noiseIntensity;
                uniform float noiseScale;
                uniform float time;

                float random(vec2 st) {
                    return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
                }

                float rand(float n){return fract(sin(n) * 43758.5453123);}

                float noise(float p){
                    float fl = floor(p);
                    float fc = fract(p);
                    return mix(rand(fl), rand(fl + 1.0), fc);
                }
            `,
        );
        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <dithering_fragment>',
            /* glsl */ `
                #include <dithering_fragment>
                vec3 viewDirection = normalize(cameraPosition - vWorldPosition);
                float normalDotView = dot(normalize(vWorldNormal), viewDirection);

                if (normalDotView < 0.0) {
                    vec3 blendColor = vec3(1.0, 1.0, 1.0);
                    gl_FragColor.rgb = mix(gl_FragColor.rgb, blendColor, backfaceIntensity);
                }
            `,
        );
    };
    material.needsUpdate = true;
    return material;
}

export { applyGlassBackfaceShader };
